import { DndContext, DragOverlay, closestCorners } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import { Combobox } from "@headlessui/react";
import { createPortal } from "react-dom";
import { t } from "@/i18n-js/instance";
import { TippyV2 } from "@circle-react/components/shared/TippyV2";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { Topic } from "@circle-react/types/Topic";
import { IconButton } from "@circle-react-shared/uikit/HeaderV3/IconButton";
import { DropdownOptions } from "./DropdownOptions";
import { TopicInput } from "./Input";
import { SelectedTopic } from "./SelectedTopic";
import { SortableSelectedTopic } from "./SortableSelectedTopic";
import { MAX_TOPICS_PER_POST } from "./constants";
import { useTopicsDragAndDrop } from "./hooks/useTopicsDragAndDrop";
import { useTopicsDropdown } from "./hooks/useTopicsDropdown";
import { useTopicsManager } from "./hooks/useTopicsManager";
import { useTopicsPermissions } from "./hooks/useTopicsPermissions";
import { useTopicsSearch } from "./hooks/useTopicsSearch";
import type { ActiveOption } from "./types";
import "./styles.scss";

export interface TopicsPickerProps {
  containerClassName?: string;
  creatable?: boolean;
  deletable?: boolean;
  maxTopicsAllowed?: number;
  onChange?: (value: number[]) => void;
  sortable?: boolean;
  spaceId?: string;
  value?: number[];
}

export const TopicsPicker = ({
  value = [],
  onChange,
  spaceId,
  maxTopicsAllowed = MAX_TOPICS_PER_POST,
  sortable = false,
  creatable = false,
  deletable = true,
  containerClassName,
}: TopicsPickerProps) => {
  const {
    canSelectTopic,
    canRemoveTopic,
    canCreateTopics,
    canRemoveAllTopics,
  } = useTopicsPermissions({ creatable, deletable });

  const { searchQuery, setSearchQuery, resetSearchInput, searchInputRef } =
    useTopicsSearch();

  const {
    selectedTopics,
    selectedTopicIds,
    updateSelectedTopics,
    hasTopicsForPicker,
    hasTopicsSelected,
    hasMaxTopicsSelected,
    removeTopic,
    removeLastTopic,
    removeAllTopics,
    isCreatingTopic,
    createTopic,
  } = useTopicsManager({
    spaceId,
    value,
    onChange,
    onTopicCreated: resetSearchInput,
    maxTopicsAllowed,
    sortable,
    canRemoveTopic,
  });

  const {
    isLoading: isLoadingDropdownTopics,
    dropdownTopics,
    shouldShowCreateTopic,
    shouldShowDropdown,
    styles,
    attributes,
    popperRef,
    buttonRef,
  } = useTopicsDropdown({
    spaceId,
    searchQuery,
    selectedTopics,
    hasMaxTopicsSelected,
    canSelectTopic,
    canCreateTopics,
    searchInputRef,
  });

  const { draggedTopic, handleDragStart, handleDragEnd, sensors } =
    useTopicsDragAndDrop({
      topics: selectedTopics,
      onReorderTopics: updateSelectedTopics,
    });

  if (!hasTopicsForPicker && !canCreateTopics) {
    return null;
  }

  return (
    <Combobox
      value={selectedTopics}
      onChange={(topics: Topic[]) => {
        updateSelectedTopics(topics);
        resetSearchInput();
      }}
      multiple
      nullable
    >
      {({ activeOption }: { activeOption: ActiveOption }) => (
        <div
          className={classNames(
            "text-dark flex items-start justify-between p-2",
            containerClassName,
          )}
        >
          <div
            id="quickpost-topics-picker-sortable"
            className="w-full"
            data-testid="topics-list"
          >
            <Combobox.Button className="hidden" ref={buttonRef} />
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={selectedTopicIds}
                strategy={rectSortingStrategy}
                disabled={!sortable}
              >
                <div className="flex w-full flex-row flex-wrap items-center gap-2">
                  {selectedTopics.map(topic => (
                    <SortableSelectedTopic
                      key={topic.id}
                      topic={topic}
                      isDraggable={sortable}
                      isRemovable={canRemoveTopic(topic)}
                      onRemove={() => removeTopic(topic)}
                    />
                  ))}
                  {!hasMaxTopicsSelected && (
                    <TopicInput
                      value={searchQuery}
                      onChange={setSearchQuery}
                      activeOption={activeOption}
                      maxTopicsAllowed={maxTopicsAllowed}
                      isCreatingTopic={isCreatingTopic}
                      onCreateTopic={createTopic}
                      onRemoveLastTopic={removeLastTopic}
                      onRemoveAllTopics={removeAllTopics}
                      searchInputRef={searchInputRef}
                      buttonRef={buttonRef}
                    />
                  )}
                </div>
              </SortableContext>
              {createPortal(
                <DragOverlay zIndex={99999}>
                  {draggedTopic && (
                    <SelectedTopic
                      topic={draggedTopic}
                      isDragging
                      isRemovable={canRemoveTopic(draggedTopic)}
                      onRemove={() => removeTopic(draggedTopic)}
                    />
                  )}
                </DragOverlay>,
                document.body,
              )}
            </DndContext>
            <DropdownOptions
              searchQuery={searchQuery}
              topics={dropdownTopics}
              isLoading={isLoadingDropdownTopics}
              isCreatingTopic={isCreatingTopic}
              onCreateTopic={createTopic}
              shouldShowCreateTopic={shouldShowCreateTopic}
              shouldShowDropdown={shouldShowDropdown}
              style={styles.popper}
              attributes={attributes.popper}
              popperRef={popperRef}
            />
          </div>
          {hasTopicsSelected && canRemoveAllTopics && (
            <TippyV2
              content={t("quick_post_v2.topics.remove_all")}
              placement="top-end"
            >
              <IconButton
                className="size-7"
                iconClassName="!text-dark"
                onClick={removeAllTopics}
                name="16-trash-bin-2"
                ariaLabel={t("quick_post_v2.topics.remove_all")}
              />
            </TippyV2>
          )}
        </div>
      )}
    </Combobox>
  );
};
